/* Universal Report Center — cross-product report management for every Liturgical Intelligence™ product.
 * -----------------------------------------------------------------------------------
 * P2 refinement (2026-02-11, owner-approved narrow scope):
 *   • Enlarged page identity ("Report Center" + supporting line)
 *   • Bronze primary CTA: New Report
 *   • Per-dossier action strip = Open (primary bronze) + PDF + DOCX visible;
 *     Duplicate · Regenerate · Share · Delete live inside a "More actions" menu.
 *     Delete is NEVER a routine equal-weight action.
 *   • Strengthened dossier title + liturgical occasion/date; muted gen-time/credits.
 *   • Improved small-label contrast (Inter, ink meta, no Jost).
 *   • Tablet/mobile: filter row collapses cleanly (chip trigger + sheet), no tiny compress.
 *   • Delete = parchment confirmation modal (matches SundayBuilder StartOverConfirm).
 * Structure preserved: page architecture, filter row, archive count, horizontal dossier cards. */

import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search, Loader2, FileText, ExternalLink, Copy, Download, RefreshCw,
  Share2, Trash2, Plus, ArrowLeft, MoreHorizontal, X, SlidersHorizontal,
} from "lucide-react";
import { track } from "../lib/track";
import RubricsAuditBadge from "../components/RubricsAuditBadge";
import "./ReportHistory.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_LABEL = {
  sundaybuilder: "SundayBuilder™",
  parish_intelligence: "Parish Intelligence™",
};
const STATUS_LABEL = { completed: "Completed", failed: "Failed", in_progress: "In Progress" };

const uniq = (arr) => Array.from(new Set(arr.filter(Boolean))).sort();

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};
const fmtDur = (ms) => (ms == null ? "—" : `${(ms / 1000).toFixed(1)}s`);

export default function ReportHistory() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  const [product, setProduct] = useState("");
  const [status, setStatus] = useState("");
  const [denomination, setDenomination] = useState("");
  const [occasion, setOccasion] = useState("");
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Per-row action state
  const [acting, setActing] = useState({});
  const [shareInfo, setShareInfo] = useState(null);
  const [rowError, setRowError] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null); // report to delete

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reports`);
      setReports(r.data.reports || []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { track("Report Center Viewed"); load(); }, [load]);

  const denomOptions = useMemo(() => uniq(reports.map((r) => r.denomination)), [reports]);
  const occasionOptions = useMemo(() => uniq(reports.map((r) => r.liturgical_occasion)), [reports]);

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      if (product && r.product !== product) return false;
      if (status && r.status !== status) return false;
      if (denomination && r.denomination !== denomination) return false;
      if (occasion && r.liturgical_occasion !== occasion) return false;
      if (q) {
        const s = q.toLowerCase();
        const hay = [r.title, r.church, r.liturgical_occasion, r.denomination, r.id, r.scope]
          .filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [reports, product, status, denomination, occasion, q]);

  const activeAdvancedCount = [product, status, denomination, occasion].filter(Boolean).length;
  const hasFilters = !!(q || product || status || denomination || occasion);
  const clearFilters = () => {
    setQ(""); setProduct(""); setStatus(""); setDenomination(""); setOccasion("");
    setFilterSheetOpen(false);
  };

  const setActingFor = (id, kind) => setActing((p) => ({ ...p, [id]: kind || null }));
  const setRowErr = (id, msg) => setRowError((p) => ({ ...p, [id]: msg || null }));

  const openReport = (r) => {
    if (r.status !== "completed") return;
    navigate(`/reports/${r.id}`);
  };

  const duplicate = async (r) => {
    setActingFor(r.id, "duplicate"); setRowErr(r.id, null);
    try {
      await axios.post(`${API}/reports/${r.id}/duplicate`);
      await load();
      track("Report Duplicated", { id: r.id });
    } catch (e) {
      setRowErr(r.id, e.response?.data?.detail || "Could not duplicate this report.");
    }
    setActingFor(r.id, null);
  };

  const regenerate = async (r) => {
    setActingFor(r.id, "regenerate"); setRowErr(r.id, null);
    try {
      await axios.post(`${API}/reports/${r.id}/regenerate`, {});
      await load();
      track("Report Regeneration Started", { id: r.id, product: r.product });
    } catch (e) {
      setRowErr(r.id, e.response?.data?.detail || "Could not start regeneration.");
    }
    setActingFor(r.id, null);
  };

  const download = async (r, format) => {
    setActingFor(r.id, `export-${format}`); setRowErr(r.id, null);
    try {
      const resp = await axios.get(`${API}/reports/${r.id}/export?format=${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (r.title || "report").replace(/[^\w -]+/g, "_").slice(0, 60);
      a.download = `${safe}.${format}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      track("Report Exported", { id: r.id, format });
    } catch (e) {
      setRowErr(r.id, e.response?.data?.detail || `Could not export ${format.toUpperCase()}.`);
    }
    setActingFor(r.id, null);
  };

  const share = async (r) => {
    setActingFor(r.id, "share"); setRowErr(r.id, null);
    try {
      const resp = await axios.post(`${API}/reports/${r.id}/share`);
      setShareInfo({ url: resp.data.url, copied: false });
      track("Report Shared", { id: r.id });
    } catch (e) {
      setRowErr(r.id, e.response?.data?.detail || "Could not create share link.");
    }
    setActingFor(r.id, null);
  };

  const requestDelete = (r) => setConfirmDelete(r);
  const performDelete = async () => {
    const r = confirmDelete;
    if (!r) return;
    setConfirmDelete(null);
    setActingFor(r.id, "delete");
    try {
      await axios.delete(`${API}/reports/${r.id}`);
      setReports((prev) => prev.filter((x) => x.id !== r.id));
      track("Report Deleted", { id: r.id });
    } catch (e) {
      setRowErr(r.id, e.response?.data?.detail || "Could not delete this report.");
    }
    setActingFor(r.id, null);
  };

  const copyShare = () => {
    if (!shareInfo) return;
    navigator.clipboard.writeText(shareInfo.url).then(() => {
      setShareInfo({ ...shareInfo, copied: true });
      setTimeout(() => setShareInfo((s) => (s ? { ...s, copied: false } : s)), 2000);
    });
  };

  return (
    <div className="rc" data-testid="report-center">
      <header className="rc-top">
        <div className="rc-top-inner">
          <div className="rc-top-l">
            <button className="rc-back" onClick={() => navigate("/dashboard")} data-testid="rc-back">
              <ArrowLeft size={14} /> Dashboard
            </button>
            <div className="rc-title-wrap">
              <h1 className="rc-title">Report Archive</h1>
              <p className="rc-sub">Every worship plan and dossier in your parish archive.</p>
            </div>
          </div>
          <div className="rc-actions">
            <button
              className="rc-new"
              onClick={() => navigate("/worship-planner")}
              data-testid="rc-new-report"
            >
              <Plus size={16} strokeWidth={2.4} /> New report
            </button>
          </div>
        </div>
      </header>

      <main className="rc-body">
        {/* Desktop filter row — preserved architecture (search + 4 selects) */}
        <div className="rc-filters" data-testid="rc-filters">
          <div className="rc-search">
            <Search size={16} aria-hidden="true" />
            <input
              placeholder="Search title, church, occasion, denomination, or ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="rc-search"
              aria-label="Search reports"
            />
            {q && (
              <button
                className="rc-search-clear"
                onClick={() => setQ("")}
                aria-label="Clear search"
                type="button"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="rc-filter-select">
            <label className="rc-filter-label" htmlFor="rc-f-product">Product</label>
            <select id="rc-f-product" value={product} onChange={(e) => setProduct(e.target.value)} data-testid="rc-filter-product">
              <option value="">All products</option>
              <option value="sundaybuilder">SundayBuilder™</option>
              <option value="parish_intelligence">Parish Intelligence™</option>
            </select>
          </div>
          <div className="rc-filter-select">
            <label className="rc-filter-label" htmlFor="rc-f-status">Status</label>
            <select id="rc-f-status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="rc-filter-status">
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="in_progress">In Progress</option>
            </select>
          </div>
          <div className="rc-filter-select">
            <label className="rc-filter-label" htmlFor="rc-f-denom">Denomination</label>
            <select id="rc-f-denom" value={denomination} onChange={(e) => setDenomination(e.target.value)} data-testid="rc-filter-denom">
              <option value="">All denominations</option>
              {denomOptions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="rc-filter-select">
            <label className="rc-filter-label" htmlFor="rc-f-occ">Occasion</label>
            <select id="rc-f-occ" value={occasion} onChange={(e) => setOccasion(e.target.value)} data-testid="rc-filter-occasion">
              <option value="">All occasions</option>
              {occasionOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        {/* Mobile/tablet filter trigger — replaces the compressed grid at ≤900px */}
        <div className="rc-filters-mobile">
          <div className="rc-search rc-search-mobile">
            <Search size={16} aria-hidden="true" />
            <input
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="rc-search-mobile"
              aria-label="Search reports"
            />
          </div>
          <button
            type="button"
            className="rc-filters-toggle"
            onClick={() => setFilterSheetOpen(true)}
            data-testid="rc-filters-toggle"
            aria-expanded={filterSheetOpen}
          >
            <SlidersHorizontal size={15} strokeWidth={2.2} />
            Filters{activeAdvancedCount > 0 ? ` (${activeAdvancedCount})` : ""}
          </button>
        </div>

        <div className="rc-toolbar">
          <div className="rc-count" data-testid="rc-count">
            {loading ? "Loading…" : `${filtered.length} dossier${filtered.length === 1 ? "" : "s"}${hasFilters ? ` (of ${reports.length})` : ""}`}
          </div>
          {hasFilters && (
            <button className="rc-clear" onClick={clearFilters} data-testid="rc-clear-filters">
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="rc-loading"><Loader2 size={18} className="rc-spin" /> Loading your reports…</div>
        ) : filtered.length === 0 ? (
          <div className="rc-empty" data-testid="rc-empty">
            <FileText size={44} />
            <h2>{reports.length === 0 ? "No reports yet" : "No reports match your filters"}</h2>
            <p>
              {reports.length === 0
                ? "Generate a worship plan or Parish Intelligence report and it will appear here automatically."
                : "Try clearing filters or adjusting your search."}
            </p>
            {reports.length === 0 ? (
              <button className="rc-empty-cta" onClick={() => navigate("/worship-planner")} data-testid="rc-empty-cta">
                <Plus size={14} /> Generate your first report
              </button>
            ) : (
              <button className="rc-empty-cta" onClick={clearFilters}>Clear filters</button>
            )}
          </div>
        ) : (
          <div className="rc-list" data-testid="rc-list">
            {filtered.map((r) => (
              <ReportCard
                key={r.id}
                report={r}
                acting={acting[r.id]}
                error={rowError[r.id]}
                onOpen={() => openReport(r)}
                onDuplicate={() => duplicate(r)}
                onRegenerate={() => regenerate(r)}
                onDownloadPdf={() => download(r, "pdf")}
                onDownloadDocx={() => download(r, "docx")}
                onShare={() => share(r)}
                onDelete={() => requestDelete(r)}
                onDismissError={() => setRowErr(r.id, null)}
              />
            ))}
          </div>
        )}
      </main>

      {/* Mobile filter sheet */}
      {filterSheetOpen && (
        <FilterSheet
          product={product} setProduct={setProduct}
          status={status} setStatus={setStatus}
          denomination={denomination} setDenomination={setDenomination}
          occasion={occasion} setOccasion={setOccasion}
          denomOptions={denomOptions} occasionOptions={occasionOptions}
          onClose={() => setFilterSheetOpen(false)}
          onClear={clearFilters}
          activeCount={activeAdvancedCount}
        />
      )}

      {/* Share dialog */}
      {shareInfo && (
        <div
          className="rc-modal-backdrop"
          onClick={() => setShareInfo(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="rc-share-title"
          data-testid="rc-share-dialog"
        >
          <div className="rc-modal" onClick={(e) => e.stopPropagation()}>
            <h3 id="rc-share-title" className="rc-modal__title">Share this dossier</h3>
            <p className="rc-modal__body">Anyone with this link can view the dossier read-only. Delete the dossier to revoke access.</p>
            <div className="rc-share-url">
              <input readOnly value={shareInfo.url} data-testid="rc-share-url" aria-label="Share link" />
              <button onClick={copyShare} className={shareInfo.copied ? "ok" : ""} data-testid="rc-share-copy">
                {shareInfo.copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="rc-modal__actions">
              <button className="rc-btn ghost" onClick={() => setShareInfo(null)} data-testid="rc-share-close">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <DeleteConfirm
          report={confirmDelete}
          onKeep={() => setConfirmDelete(null)}
          onDiscard={performDelete}
        />
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Dossier card
 * ------------------------------------------------------------------------- */
function ReportCard({
  report, acting, error, onOpen,
  onDuplicate, onRegenerate, onDownloadPdf, onDownloadDocx, onShare, onDelete, onDismissError,
}) {
  const busy = (kind) => acting === kind;
  const anyBusy = !!acting;
  const isCompleted = report.status === "completed";

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  // Close menu on outside click + ESC
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;
      setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleCardClick = (e) => {
    if (e.target.closest("button, a, [role='menu'], [role='menuitem']")) return;
    onOpen();
  };

  const handleMenuAction = (fn) => {
    setMenuOpen(false);
    fn();
  };

  return (
    <div
      className="rc-card"
      onClick={handleCardClick}
      data-testid={`rc-card-${report.id}`}
      data-status={report.status}
      role="article"
    >
      <div className="rc-card-h">
        <div className="rc-card-title-wrap">
          <h3 className="rc-card-title" data-testid="rc-card-title">{report.title}</h3>
          {report.liturgical_occasion && (
            <div className="rc-card-occasion" data-testid="rc-card-occasion">
              {report.liturgical_occasion}
            </div>
          )}
          {report.church && <div className="rc-card-church">{report.church}</div>}
        </div>
        <span className={`rc-status s-${report.status}`} data-testid="rc-card-status">
          <span className="dot" aria-hidden="true" />
          {STATUS_LABEL[report.status] || report.status}
        </span>
      </div>

      <div className="rc-badges">
        <span className={`rc-badge b-product-${report.product}`}>{PRODUCT_LABEL[report.product] || report.product}</span>
        {report.scope && <span className="rc-badge">{report.scope}</span>}
        {report.denomination && <span className="rc-badge">{report.denomination}</span>}
        {report.source === "duplicate" && (
          <span className="rc-badge b-duplicate" data-testid="rc-card-duplicate-badge">
            <Copy size={11} strokeWidth={2.2} /> Duplicate
          </span>
        )}
        {report.product === "sundaybuilder" && report.rubric_badge && (
          <RubricsAuditBadge badge={report.rubric_badge} onOpenAudit={onOpen} />
        )}
      </div>

      <div className="rc-card-meta">
        <span className="rc-meta-primary"><b>Prepared</b> {fmtDate(report.generated_at)}</span>
        <span className="rc-meta-quiet">Gen time {fmtDur(report.generation_ms)}</span>
        <span className="rc-meta-quiet">Credits {report.credits_used}</span>
      </div>

      {error && (
        <div className="rc-card-error" data-testid={`rc-card-err-${report.id}`}>
          <span>{error}</span>
          <button type="button" className="rc-card-error-dismiss" onClick={onDismissError} aria-label="Dismiss error">
            <X size={12} />
          </button>
        </div>
      )}

      <div className="rc-card-actions" onClick={(e) => e.stopPropagation()}>
        <button
          className="rc-primary"
          onClick={onOpen}
          disabled={!isCompleted}
          data-testid={`rc-open-${report.id}`}
        >
          <ExternalLink size={13} strokeWidth={2.4} /> Open
        </button>
        <button
          className="rc-secondary"
          onClick={onDownloadPdf}
          disabled={!isCompleted || anyBusy}
          data-testid={`rc-pdf-${report.id}`}
        >
          {busy("export-pdf") ? <Loader2 size={13} className="rc-spin" /> : <Download size={13} strokeWidth={2.2} />} PDF
        </button>
        <button
          className="rc-secondary"
          onClick={onDownloadDocx}
          disabled={!isCompleted || anyBusy}
          data-testid={`rc-docx-${report.id}`}
        >
          {busy("export-docx") ? <Loader2 size={13} className="rc-spin" /> : <Download size={13} strokeWidth={2.2} />} DOCX
        </button>

        {/* More actions menu — houses Duplicate · Regenerate · Share · Delete */}
        <div className="rc-more-wrap">
          <button
            ref={triggerRef}
            type="button"
            className="rc-more"
            onClick={() => setMenuOpen((v) => !v)}
            disabled={anyBusy}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label="More actions"
            data-testid={`rc-more-${report.id}`}
          >
            <MoreHorizontal size={16} strokeWidth={2.2} /> More
          </button>
          {menuOpen && (
            <div
              ref={menuRef}
              className="rc-more-menu"
              role="menu"
              data-testid={`rc-more-menu-${report.id}`}
            >
              <button
                role="menuitem"
                onClick={() => handleMenuAction(onDuplicate)}
                disabled={!isCompleted}
                data-testid={`rc-menu-duplicate-${report.id}`}
              >
                {busy("duplicate") ? <Loader2 size={13} className="rc-spin" /> : <Copy size={13} strokeWidth={2.2} />}
                Duplicate
              </button>
              <button
                role="menuitem"
                onClick={() => handleMenuAction(onRegenerate)}
                data-testid={`rc-menu-regenerate-${report.id}`}
              >
                {busy("regenerate") ? <Loader2 size={13} className="rc-spin" /> : <RefreshCw size={13} strokeWidth={2.2} />}
                Regenerate
              </button>
              <button
                role="menuitem"
                onClick={() => handleMenuAction(onShare)}
                disabled={!isCompleted}
                data-testid={`rc-menu-share-${report.id}`}
              >
                {busy("share") ? <Loader2 size={13} className="rc-spin" /> : <Share2 size={13} strokeWidth={2.2} />}
                Share link
              </button>
              <div className="rc-menu-divider" role="separator" />
              <button
                role="menuitem"
                className="danger"
                onClick={() => handleMenuAction(onDelete)}
                data-testid={`rc-menu-delete-${report.id}`}
              >
                <Trash2 size={13} strokeWidth={2.2} />
                Delete dossier…
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Filter sheet — mobile/tablet only. Full filters at readable size.
 * ------------------------------------------------------------------------- */
function FilterSheet({
  product, setProduct, status, setStatus, denomination, setDenomination, occasion, setOccasion,
  denomOptions, occasionOptions, onClose, onClear, activeCount,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="rc-modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rc-filter-sheet-title"
      data-testid="rc-filter-sheet"
    >
      <div className="rc-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="rc-sheet-h">
          <h3 id="rc-filter-sheet-title" className="rc-modal__title">Filters</h3>
          <button className="rc-icon-btn" onClick={onClose} aria-label="Close filters" data-testid="rc-filter-sheet-close">
            <X size={16} />
          </button>
        </div>

        <div className="rc-sheet-field">
          <label htmlFor="rc-sheet-product">Product</label>
          <select id="rc-sheet-product" value={product} onChange={(e) => setProduct(e.target.value)} data-testid="rc-filter-product-sheet">
            <option value="">All products</option>
            <option value="sundaybuilder">SundayBuilder™</option>
            <option value="parish_intelligence">Parish Intelligence™</option>
          </select>
        </div>
        <div className="rc-sheet-field">
          <label htmlFor="rc-sheet-status">Status</label>
          <select id="rc-sheet-status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="rc-filter-status-sheet">
            <option value="">All statuses</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
            <option value="in_progress">In Progress</option>
          </select>
        </div>
        <div className="rc-sheet-field">
          <label htmlFor="rc-sheet-denom">Denomination</label>
          <select id="rc-sheet-denom" value={denomination} onChange={(e) => setDenomination(e.target.value)} data-testid="rc-filter-denom-sheet">
            <option value="">All denominations</option>
            {denomOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="rc-sheet-field">
          <label htmlFor="rc-sheet-occ">Occasion</label>
          <select id="rc-sheet-occ" value={occasion} onChange={(e) => setOccasion(e.target.value)} data-testid="rc-filter-occasion-sheet">
            <option value="">All occasions</option>
            {occasionOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <div className="rc-sheet-actions">
          <button className="rc-btn ghost" onClick={onClear} disabled={activeCount === 0} data-testid="rc-filter-sheet-clear">
            Clear
          </button>
          <button className="rc-btn primary" onClick={onClose} data-testid="rc-filter-sheet-apply">
            Show results
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Delete confirmation modal — parchment shell, default focus = Keep.
 * ------------------------------------------------------------------------- */
function DeleteConfirm({ report, onKeep, onDiscard }) {
  const keepRef = useRef(null);
  useEffect(() => {
    keepRef.current?.focus();
    const esc = (e) => { if (e.key === "Escape") onKeep(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onKeep]);

  return (
    <div
      className="rc-modal-backdrop"
      onClick={onKeep}
      role="dialog"
      aria-modal="true"
      aria-labelledby="rc-delete-title"
      data-testid="rc-delete-confirm-modal"
    >
      <div className="rc-modal" onClick={(e) => e.stopPropagation()}>
        <h2 id="rc-delete-title" className="rc-modal__title">Discard this dossier?</h2>
        <p className="rc-modal__body">
          <span className="rc-modal__quoted">"{report.title}"</span> will be permanently
          removed from your archive. This cannot be undone.
        </p>
        <div className="rc-modal__actions">
          <button
            ref={keepRef}
            className="rc-btn ghost"
            onClick={onKeep}
            data-testid="rc-delete-keep"
          >
            Keep
          </button>
          <button
            className="rc-btn destructive"
            onClick={onDiscard}
            data-testid="rc-delete-discard"
          >
            Discard dossier
          </button>
        </div>
      </div>
    </div>
  );
}
